import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Hotel,
  HotelDetails,
  SearchParameters,
  SocketEvent,
  SortHotelsBy,
  wsAvailability,
} from "../../assets/Services/wsSearch";
import { fireTagManagerEvent } from "../../assets/Services/tagmanager";
import { getSocketInstance } from "../../assets/Utils/socket";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  dateToSearchFormat,
  removeKeys,
  sortHotels,
} from "../pages/Directsearch/utils";
import {
  getHotelDetails,
  getQueryString,
  getViewport,
  searchHotels,
} from "../../assets/Utils/search";
import { Path } from "../../config/paths";
import { closeModal } from "../../assets/Utils/modal";
import { LatLng } from "react-google-places-autocomplete/build/types";
import { sha256 } from "sha.js";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  ANALYTICS_SORT_BY,
  ANALYTICS_SOURCE,
  ANALYTICS_SEARCH_TYPE,
} from "../../config/analytics";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/userSlice";

type Viewport = {
  northeast: LatLng;
  southwest: LatLng;
};

const DEFAULT_SIZE = 20;

export const DEFAULT_PARAMS: SearchParameters = {
  arrivalDate: dateToSearchFormat(new Date()),
  departureDate: dateToSearchFormat(
    new Date(new Date().setDate(new Date().getDate() + 1)),
  ),
  searchType: "city",
  adults: 2,
  childrenAge: [],
  size: DEFAULT_SIZE,
};

const SearchContext = createContext(
  {} as {
    searchParameters: SearchParameters;
    currentSearchParameters: SearchParameters; // TODO: assess if this is needed
    setParameters: Dispatch<SetStateAction<SearchParameters>>;

    hotels: Hotel[];
    nearHotels: Hotel[];
    unavailableHotels: Hotel[];
    hotelDetail?: HotelDetails;
    viewport?: Viewport;

    search: (params: SearchParameters, isLoadMore?: boolean) => void;
    getHotelDetails: (id: string) => void;
    loadMore: () => void;
    handleSortChange: (
      by: SortHotelsBy | undefined,
      source: (typeof ANALYTICS_SOURCE)[keyof typeof ANALYTICS_SOURCE],
    ) => void;
    goToSearchUrl: (
      params: Partial<SearchParameters>,
      options?: {
        reset?: boolean;
        triggerTagManagerEvent?: boolean;
        refresh?: boolean;
        source: (typeof ANALYTICS_SOURCE)[keyof typeof ANALYTICS_SOURCE];
        searchType?: (typeof ANALYTICS_SEARCH_TYPE)[keyof typeof ANALYTICS_SEARCH_TYPE];
      },
    ) => void;

    buildRedirectURL: (collectionId: string, fallbackUrl: string) => string;

    isLoading: boolean;
    isSearchingNearHotels: boolean;
    loadingProgress: number;
    allResults: boolean;
    isLoadingDetails: boolean;
    isFirstSearch: boolean;

    startConnection: () => void;
    disconnectListeners: () => void;
  },
);

export function SearchProvider({ children }: { children: JSX.Element }) {
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();
  const socket = getSocketInstance();

  const paramsObject: SearchParameters | undefined = useMemo(() => {
    const array = Array.from(urlParams.entries()).map(
      ([k, v]: [string, string]) => [
        k,
        parseParams(k as keyof SearchParameters, v),
      ],
    );
    if (array.length === 0) return;
    return Object.fromEntries(array) as any;
  }, [urlParams]);

  const [isFirstSearch, setIsFirstSearch] = useState(true);

  // This is to link the last search done with the results
  const searchId = useRef<string>("");

  const [isLoading, setIsLoading] = useState(true);
  const [viewport, setViewport] = useState<Viewport>();

  const initialState = { ...DEFAULT_PARAMS, ...paramsObject };
  const [searchParameters, setParameters] = useState(initialState);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [unavailableHotels, setUnavailableHotels] = useState<Hotel[]>([]);
  const [nearHotels, setNearHotels] = useState<Hotel[]>([]);
  const [hotelDetail, setHotelDetail] = useState<HotelDetails>();
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isSearchingNearHotels, setIsSearchingNearHotels] = useState(false);
  const [loadMoreSkip, setLoadMoreSkip] = useState(0);
  const [ipAddress, setIpAddress] = useState<string | undefined>();
  const [allResults, setAllResults] = useState(false);

  const getIpAddress = useCallback(async () => {
    if (ipAddress) return ipAddress;

    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data: { ip: string } = await res.json();
      setIpAddress(data.ip);
      return data.ip;
    } catch {}
  }, [ipAddress]);

  // If the url changes, we need to trigger a new search
  useEffect(() => {
    // We could avoid doing this by only importing the searchProvider in the selected pages,
    // however, we need to it like this because of how we manage modals (which are "global")
    if (window.location.pathname !== Path.DIRECTSEARCH_RESULTS) return;
    if (!paramsObject) return;
    console.log("set params useEffect");
    setParameters(paramsObject);
    search(paramsObject);
  }, [paramsObject]);

  const startConnection = () => {
    if (socket.connected) return;
    socket.connect();
  };

  // When starting a new search, we need to instantiate new listeners and disconnect the old ones
  const refreshListeners = (oldId: string, newId: string) => {
    disconnectListeners(oldId);
    searchId.current = newId;

    setIsLoading(true);

    socket.on(`${SocketEvent.HOTEL}_${newId}`, (hotel: Hotel) => {
      setLoadingProgress(
        (p) => p + 1 / (searchParameters.size ?? DEFAULT_SIZE),
      );
      setHotels((p) =>
        sortHotels(
          p,
          hotel,
          searchParameters.sortBy ?? SortHotelsBy.RECOMMENDED,
          searchParameters.searchLocationType === "hotel" ? searchParameters.destinationLabel : undefined
        ),
      );
    });
    socket.on(`${SocketEvent.UAVAILABLE}_${newId}`, (hotel: Hotel) => {
      setLoadingProgress(
        (p) => p + 1 / (searchParameters.size ?? DEFAULT_SIZE),
      );
      setUnavailableHotels((p) =>
        sortHotels(
          p,
          hotel,
          searchParameters.sortBy ?? SortHotelsBy.RECOMMENDED,
          searchParameters.searchLocationType === "hotel" ? searchParameters.destinationLabel : undefined
        ),
      );
    });

    socket.on(
      `${SocketEvent.VIEWPORT}_${newId}`,
      ({ viewport }: { viewport: Viewport }) => setViewport(viewport),
    );

    socket.on(`${SocketEvent.SEARCHING_NEAR_HOTELS}_${newId}`, () =>
      setIsSearchingNearHotels(true),
    );

    socket.on(`${SocketEvent.NEAR_HOTEL}_${newId}`, (hotel: Hotel) => {
      setNearHotels((p) =>
        sortHotels(
          p,
          hotel,
          searchParameters.sortBy ?? SortHotelsBy.RECOMMENDED,
          searchParameters.searchLocationType === "hotel" ? searchParameters.destinationLabel : undefined
        ),
      );
    });

    socket.on(
      `${SocketEvent.DONE}_${newId}`,
      ({ count, allResults }: { count: number; allResults: boolean }) => {
        setLoadMoreSkip(count);
        setLoadingProgress(1);
        setIsLoading(false);
        setAllResults(allResults);
      },
    );
  };

  const disconnectListeners = (id = searchId.current) => {
    socket.off(`${SocketEvent.HOTEL}_${id}`);
    socket.off(`${SocketEvent.VIEWPORT}_${id}`);
    socket.off(`${SocketEvent.NEAR_HOTEL}_${id}`);
    socket.off(`${SocketEvent.SEARCHING_NEAR_HOTELS}_${id}`);
    socket.off(`${SocketEvent.DONE}_${id}`);
  };

  /**
   * Performs a search call via websocket
   *
   * `params` - If passed, uses these as search parameters, otherwise uses the object saved in state
   */
  const search = useCallback(
    async (params: SearchParameters, isLoadMore?: boolean) => {
      if (!socket.connected) socket.connect();
      const ip = await getIpAddress();

      const _params = params;

      const entries = Object.entries(_params).filter(([_, v]) => !!v);
      const searchParams = entries.length > 0 ? _params : null;
      if (!searchParams) return;

      if (!isLoadMore) {
        if (!searchParams.boxCoordinates) {
          if (searchParams.placeId) {
            const vp = await getViewport(searchParams.placeId);
            setViewport(vp);
          }
        } else {
          setViewport(searchParams.boxCoordinates);
        }
        setHotels([]);
        setNearHotels([]);
        setUnavailableHotels([]);
        setIsSearchingNearHotels(false);
      }

      setLoadingProgress(0);
      setAllResults(false);

      const newId = new sha256()
        .update(JSON.stringify({ ..._params, ip }))
        .digest("hex");
      if (newId === searchId.current) return;

      socket.emit(SocketEvent.SEARCH, {
        id: newId,
        params: searchParams,
      });

      refreshListeners(searchId.current, newId);
      setIsFirstSearch(false);
    },
    [searchParameters, getIpAddress],
  );

  const user = useSelector(selectUser);

  /**
   * Builds the URL of directsearch page and navigates to it
   *
   * `params` - Parameters of the search
   * `navigate` - React navigate function from `useNavigate()`
   * `options` - Options to trigger additional actions
   */
  const goToSearchUrl = useCallback(
    (
      params: Partial<SearchParameters>,
      options?: {
        reset?: boolean;
        triggerTagManagerEvent?: boolean;
        refresh?: boolean;
        source: (typeof ANALYTICS_SOURCE)[keyof typeof ANALYTICS_SOURCE];
        searchType?: (typeof ANALYTICS_SEARCH_TYPE)[keyof typeof ANALYTICS_SEARCH_TYPE];
      },
    ) => {
      let _params = { ...searchParameters, skip: undefined, ...params };

      if (_params.searchType === "city") {
        _params.boxCoordinates = undefined;
      }

      if (options?.reset) {
        _params.filters = {};
        _params.sortBy = SortHotelsBy.RECOMMENDED;
      }

      if (!_params.departureDate || !_params.arrivalDate) return;

      const queryString = getQueryString(_params);
      const target = `${Path.DIRECTSEARCH_RESULTS}?${queryString}`;

      if (options?.triggerTagManagerEvent) {

        if (searchParameters.searchType === "box") {
          fireTagManagerEvent("search_started_map", {
            user_id: user?._id ?? null,
            categories_filter: JSON.stringify(searchParameters.filters ?? {}),
            destination: !!searchParameters.destinationLabel
              ? searchParameters.destinationLabel
              : null,
            tag: !!searchParameters.tag ? searchParameters.tag : null,
            adults: searchParameters.adults ?? null,
            children: searchParameters.childrenAge.length,
            sort_type: searchParameters.sortBy
              ? ANALYTICS_SORT_BY[
                  (
                    {
                      recommended: "RECOMMENDED",
                      distance_to_cc: "DISTANCE",
                      reviews_score: "REVIEWS",
                      price: "PRICE",
                    } as const
                  )[searchParameters.sortBy]
                ]
              : ANALYTICS_SORT_BY.RECOMMENDED,
            check_in: searchParameters.arrivalDate
              ? format(new Date(searchParameters.arrivalDate), "dd-MM-yyyy", {
                  locale: it,
                })
              : null,
            check_out: searchParameters.departureDate
              ? format(new Date(searchParameters.departureDate), "dd-MM-yyyy", {
                  locale: it,
                })
              : null,
            source: options.source,
            url: `${window.location.origin}${target}`,
          });
        } else {
          fireTagManagerEvent("search_started", {
            user_id: user?._id ?? null,
            categories_filter: JSON.stringify(searchParameters.filters ?? {}),
            destination: !!searchParameters.destinationLabel
              ? searchParameters.destinationLabel
              : null,
            tag: !!searchParameters.tag ? searchParameters.tag : null,
            adults: searchParameters.adults ?? null,
            children: searchParameters.childrenAge.length,
            sort_type: searchParameters.sortBy
              ? ANALYTICS_SORT_BY[
                  (
                    {
                      recommended: "RECOMMENDED",
                      distance_to_cc: "DISTANCE",
                      reviews_score: "REVIEWS",
                      price: "PRICE",
                    } as const
                  )[searchParameters.sortBy]
                ]
              : ANALYTICS_SORT_BY.RECOMMENDED,
            check_in: searchParameters.arrivalDate
              ? format(new Date(searchParameters.arrivalDate), "dd-MM-yyyy", {
                  locale: it,
                })
              : null,
            check_out: searchParameters.departureDate
              ? format(new Date(searchParameters.departureDate), "dd-MM-yyyy", {
                  locale: it,
                })
              : null,
            source: options.source,
            search_type: searchParameters.searchType === "tag" ? ANALYTICS_SEARCH_TYPE["TAG"] 
              : options?.searchType ??
                (searchParameters.searchLocationType
                  ? ANALYTICS_SEARCH_TYPE[
                      (
                        {
                          hotel: "HOTEL",
                          known_location: "KNOWN_LOCATION",
                          nearby_location: "NEARBY_LOCATION",
                        } as const
                      )[searchParameters.searchLocationType]
                    ]
                  : null),
            url: `${window.location.origin}${target}`,
          });
        }
      }
      closeModal();

      const currentPath = window.location.pathname;
      if (Path.DIRECTSEARCH_RESULTS !== currentPath) {
        window.location.href = target;
      } else navigate(target);

      localStorage.setItem(
        "last_search",
        JSON.stringify({
          url: window.location.href,
          timestamp: new Date().toISOString(),
        }),
      );
    },
    [searchParameters, navigate],
  );

  const handleSortChange = useCallback(
    (
      by: SortHotelsBy | undefined,
      source: (typeof ANALYTICS_SOURCE)[keyof typeof ANALYTICS_SOURCE],
    ) => {
      fireTagManagerEvent("sort_results", {
        user_id: user?._id ?? null,
        sort_type: by ?? ANALYTICS_SORT_BY.RECOMMENDED,
        source,
      });
      goToSearchUrl({ ...initialState, sortBy: by });
      setParameters((p) => ({ ...p, sortBy: by }));
    },
    [goToSearchUrl, initialState],
  );

  const buildRedirectURL = useCallback(
    (businessDatabaseId: string, fallbackUrl: string) =>
      "/directsearch/redirect?" +
      getQueryString({
        ...removeKeys(searchParameters, ["boxCoordinates"]),
        collectionId: businessDatabaseId,
        fallbackUrl,
      }),
    [searchParameters],
  );

  const loadMore = useCallback(() => {
    search({ ...searchParameters, skip: loadMoreSkip }, true);
  }, [searchParameters, search, loadMoreSkip]);

  const getDetails = useCallback(async (id: string) => {
    setIsLoadingDetails(true);
    try {
      const res = await getHotelDetails(id);
      if (res) {
        setHotelDetail(res);
      } else {
        setHotelDetail(undefined);
      }
    } catch (e) {
      setHotelDetail(undefined);
    }
    setIsLoadingDetails(false);
  }, []);

  return (
    <SearchContext.Provider
      value={{
        searchParameters,
        setParameters,
        search,
        loadMore,
        hotels,
        nearHotels,
        unavailableHotels,
        hotelDetail,
        viewport,
        startConnection,
        loadingProgress,
        allResults,
        disconnectListeners,
        isLoading,
        isSearchingNearHotels,
        handleSortChange,
        buildRedirectURL,
        currentSearchParameters: initialState,
        goToSearchUrl,
        getHotelDetails: getDetails,
        isLoadingDetails,
        isFirstSearch: isFirstSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  return useContext(SearchContext);
}

export function parseParams(
  k: keyof SearchParameters,
  v: string,
): string | number | number[] {
  if (k === "childrenAge") return v ? JSON.parse(v) : [];
  else if (["filters", "boxCoordinates"].includes(k))
    return v ? JSON.parse(v) : "";
  else if (
    !isNaN(Number(v)) &&
    ["adults", "pageNumber", "pageSize", "size"].includes(k)
  )
    return Number(v);
  else return v;
}
